import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { agencyApi, type Location } from '../../api/agencyApi';
import { ModelOverridesModal } from '../ModelOverridesModal';
import styles from './ModelsCard.module.css';

/**
 * Which model (Claude, or an OpenRouter model) each AI task runs on, for one location. One table for
 * every feature — this used to be reachable from two places (Admin › Models
 * and a Models button on each location row) and is now reachable from one.
 */
export function ModelsCard({ practiceId, location }: { practiceId: string; location: Location }) {
  const [open, setOpen] = useState(false);
  const { data: registryResp } = useQuery({
    queryKey: ['agency', 'prompt-registry'],
    queryFn: () => agencyApi.getPromptRegistry(),
    staleTime: 5 * 60_000,
  });
  const registry = registryResp?.data;

  const overrides = location.anthropicModels ?? {};
  const count = Object.keys(overrides).length;
  const defaultLabel = registry
    ? (registry.models.find((m) => m.id === registry.defaultModel)?.label ?? registry.defaultModel)
    : 'account default';
  const modelLabel = (id: string) => registry?.models.find((m) => m.id === id)?.label ?? id;

  const rows = useMemo(
    () =>
      (registry?.prompts ?? []).map((prompt) => ({
        group: prompt.group,
        label: prompt.label,
        name: prompt.name,
        model: overrides[prompt.name] ?? null,
      })),
    [registry, overrides],
  );

  return (
    <Card
      title={`AI models · ${location.name}`}
      subtitle={`Every AI task this location runs. Default means the account default (${defaultLabel}).`}
      action={
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          {count > 0 ? `Edit models (${count} overridden)` : 'Edit models'}
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className={styles.empty}>
          {count === 0 ? 'Every task runs on the account default.' : `${count} task(s) overridden.`}
        </p>
      ) : (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Module</th>
                <th>Feature</th>
                <th>Model</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <td className={styles.muted}>{row.group}</td>
                  <td>{row.label}</td>
                  <td className={row.model ? styles.override : styles.muted}>
                    {row.model ? modelLabel(row.model) : `Default (${defaultLabel})`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ModelOverridesModal
        isOpen={open}
        onClose={() => setOpen(false)}
        practiceId={practiceId}
        location={location}
      />
    </Card>
  );
}
